import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SellerProductForm from '../components/SellerProductForm';
import EditProductForm from '../components/EditProductForm';
import '../styles/ProductForm.css';
import '../styles/Products.css';
import '../styles/SellerProductsPage.css';

const SellerProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [refreshData, setRefreshData] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); // Nuevo estado para el término de búsqueda
  const [filteredProducts, setFilteredProducts] = useState([]); // Nuevo estado para los productos filtrados
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          navigate('/login');
          return;
        }
        
        const response = await axios.get('/api/products/business-products', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
        setError('No se pudieron cargar los productos. Por favor, inténtelo de nuevo.');
        
        // If unauthorized, redirect to login
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [navigate, refreshData]);

  // useEffect para filtrar productos cuando searchTerm o products cambian
  useEffect(() => {
    if (searchTerm === '') {
      setFilteredProducts(products);
    } else {
      const lowercasedFilter = searchTerm.toLowerCase();
      const filtered = products.filter(product => {
        const codeMatch = product.prod_code && product.prod_code.toLowerCase().includes(lowercasedFilter);
        const nameMatch = product.prod_name && product.prod_name.toLowerCase().includes(lowercasedFilter);
        return codeMatch || nameMatch;
      });
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  const handleProductAdded = () => {
    // Refresh the products list
    setRefreshData(!refreshData);
    // Hide the form
    setShowForm(false);
    // Show success message
    setSuccessMessage('Producto agregado exitosamente');
    
    // Hide message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const handleEditClick = (product) => {
    setEditingProduct(product);
    setShowEditForm(true);
    setShowForm(false); // Cerrar el formulario de agregar si está abierto
  };

  const handleProductUpdated = () => {
    // Refresh the products list
    setRefreshData(!refreshData);
    // Hide the edit form
    setShowEditForm(false);
    setEditingProduct(null);
    // Show success message
    setSuccessMessage('Producto actualizado exitosamente');
    
    // Hide message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  if (loading && products.length === 0) {
    return <div className="loading">Cargando productos...</div>;
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <h1>Gestión de Productos</h1>
        <button onClick={() => navigate('/seller-dashboard')} className="back-btn">
          Volver al Dashboard
        </button>
      </header>

      <div className="products-section">
        <div className="products-section-header">
          <h2>Productos Registrados</h2>
          <button 
            className="add-product-btn"
            onClick={() => setShowForm(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Agregar Producto
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}

        {showForm ? (
          <SellerProductForm 
            onProductAdded={handleProductAdded} 
            onCancel={() => setShowForm(false)} 
          />
        ) : null}

        {showEditForm && editingProduct ? (
          <EditProductForm 
            product={editingProduct}
            onProductUpdated={handleProductUpdated}
            onCancel={() => {
              setShowEditForm(false);
              setEditingProduct(null);
            }}
            isSeller={true}
          />
        ) : null}

        {/* Input de búsqueda */}
        <div className="search-container" style={{ marginBottom: '20px', marginTop: '20px' }}>
          <input
            type="text"
            placeholder="Buscar por código o nombre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
        </div>

        <div className="products-list">
          {loading && filteredProducts.length === 0 && searchTerm === '' ? (
            <p className="loading-message">Cargando productos...</p>
          ) : !loading && products.length === 0 ? (
            <p className="no-products">No hay productos registrados. 
              <button onClick={() => setShowForm(true)}>Agregar Producto</button>
            </p>
          ) : filteredProducts.length === 0 && searchTerm !== '' ? (
            <p className="no-products">No se encontraron productos que coincidan con \"{searchTerm}\".</p>
          ) : filteredProducts.length === 0 && products.length > 0 && searchTerm === '' ? (
            <p className="no-products">No hay productos para mostrar.</p>
          ) : (
            <table className="products-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th>Descripción</th>
                  <th>Precio</th>
                  <th>Stock</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map(product => (
                  <tr key={product.id}>
                    <td>{product.prod_code || '-'}</td>
                    <td>{product.prod_name}</td>
                    <td>{product.prod_description || 'Sin descripción'}</td>
                    <td>${parseFloat(product.prod_price).toFixed(2)}</td>
                    <td>{product.prod_stock || 0}</td>
                    <td>
                      <span className={`status active`}>
                        Activo
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="edit-btn"
                          onClick={() => handleEditClick(product)}
                        >
                          Editar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellerProductsPage;
